<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <button
          v-if="sortMode"
          class="btn btn-success"
          :disabled="saving"
          @click="saveSortOrder"
        >
          <i class="ri-save-line"></i>
          {{ saving ? '儲存中...' : '儲存排序' }}
        </button>
        <button
          v-if="sortMode"
          class="btn btn-secondary"
          :disabled="saving"
          @click="cancelSortMode"
        >
          取消
        </button>
        <button
          v-if="!sortMode"
          class="btn btn-outline"
          @click="enterSortMode"
        >
          <i class="ri-drag-move-line"></i>
          拖拉排序
        </button>
        <button v-if="!sortMode" class="btn btn-primary" @click="handleCreate">
          <i class="ri-add-line"></i>
          新增模組
        </button>
      </div>
    </div>

    <div v-if="sortMode" class="sort-mode-hint">
      <i class="ri-information-line"></i>
      拖拉左側 <i class="ri-drag-move-2-line"></i> 圖示以調整順序，完成後點「儲存排序」
    </div>

    <div v-if="!sortMode" class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋模組代碼或模組名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchModules">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th v-if="sortMode" class="drag-col"></th>
            <th>模組代碼</th>
            <th>模組名稱</th>
            <th>適用帳號</th>
            <th>描述</th>
            <th>排序</th>
            <th>狀態</th>
            <th v-if="!sortMode">建立時間</th>
            <th v-if="!sortMode">操作</th>
          </tr>
        </thead>
        <tbody v-if="sortMode">
          <tr
            v-for="(module, index) in sortableModules"
            :key="module.id"
            class="draggable-row"
            draggable="true"
            :class="{ 'drag-over': dragOverIndex === index, 'dragging': dragIndex === index }"
            @dragstart="onDragStart(index)"
            @dragover.prevent="onDragOver(index)"
            @dragleave="onDragLeave"
            @drop="onDrop(index)"
            @dragend="onDragEnd"
          >
            <td class="drag-col">
              <i class="ri-drag-move-2-line drag-handle"></i>
            </td>
            <td>{{ module.moduleCode }}</td>
            <td>
              <span class="module-name-cell">
                <i v-if="module.icon" :class="module.icon" class="module-icon"></i>
                {{ module.moduleName }}
              </span>
            </td>
            <td>
              <span class="badge-group">
                <span
                  v-for="type in getModuleVisibleAccountTypes(module)"
                  :key="type"
                  :class="MODULE_ACCOUNT_TYPE_BADGE_CLASS[type]"
                >
                  {{ MODULE_ACCOUNT_TYPE_LABELS[type] }}
                </span>
                <span v-if="isModuleOptional(module)" class="badge badge-warning">選購</span>
              </span>
            </td>
            <td>{{ module.description || '-' }}</td>
            <td>{{ module.sortOrder }}</td>
            <td>
              <span :class="module.status === 'active' ? 'badge badge-success' : 'badge badge-danger'">
                {{ module.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-if="modules.length === 0">
            <td colspan="8" class="empty-state">沒有找到模組</td>
          </tr>
          <tr v-for="module in modules" :key="module.id">
            <td>{{ module.moduleCode }}</td>
            <td>
              <span class="module-name-cell">
                <i v-if="module.icon" :class="module.icon" class="module-icon"></i>
                {{ module.moduleName }}
              </span>
            </td>
            <td>
              <span class="badge-group">
                <span
                  v-for="type in getModuleVisibleAccountTypes(module)"
                  :key="type"
                  :class="MODULE_ACCOUNT_TYPE_BADGE_CLASS[type]"
                >
                  {{ MODULE_ACCOUNT_TYPE_LABELS[type] }}
                </span>
                <span v-if="isModuleOptional(module)" class="badge badge-warning">選購</span>
              </span>
            </td>
            <td>{{ module.description || '-' }}</td>
            <td>{{ module.sortOrder }}</td>
            <td>
              <span :class="module.status === 'active' ? 'badge badge-success' : 'badge badge-danger'">
                {{ module.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(module.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleEdit(module)" title="編輯">
                  <i class="ri-edit-line"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(module)" title="刪除">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="!sortMode && total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { moduleService } from '@/api/modules/modules.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import {
  getModuleVisibleAccountTypes,
  isModuleOptional,
  MODULE_ACCOUNT_TYPE_LABELS,
  MODULE_ACCOUNT_TYPE_BADGE_CLASS
} from '@/utils/moduleVisibility.js';

export default {
  name: 'ModuleList',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('模組列表');
    const modules = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);
    const sortMode = ref(false);
    const sortableModules = ref([]);
    const saving = ref(false);
    const dragIndex = ref(null);
    const dragOverIndex = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchModules = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        const response = await moduleService.getModules(params);

        if (response && response.success && response.data) {
          if (response.data.items) {
            modules.value = response.data.items || [];
            total.value = response.data.pagination?.totalItems || response.data.pagination?.total || response.data.total || 0;
          } else if (Array.isArray(response.data)) {
            modules.value = response.data;
            total.value = response.data.length;
          }
        } else {
          error.value = response && response.message || '獲取模組列表失敗';
        }
      } catch (err) {
        console.error('獲取模組列表失敗:', err);
        error.value = err.message || '獲取模組列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchModules();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        // 更新 URL query 參數
        router.push({
          path: route.path,
          query: { ...route.query, page: page }
        });
        fetchModules();
      }
    };

    const onDragStart = (index) => {
      dragIndex.value = index;
    };

    const onDragOver = (index) => {
      dragOverIndex.value = index;
    };

    const onDragLeave = () => {
      dragOverIndex.value = null;
    };

    const onDrop = (toIndex) => {
      const fromIndex = dragIndex.value;
      if (fromIndex === null || fromIndex === toIndex) return;
      const items = [...sortableModules.value];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      sortableModules.value = items;
      dragIndex.value = null;
      dragOverIndex.value = null;
    };

    const onDragEnd = () => {
      dragIndex.value = null;
      dragOverIndex.value = null;
    };

    const enterSortMode = async () => {
      // 排序模式載入全部資料（不分頁）
      loading.value = true;
      error.value = null;
      try {
        const response = await moduleService.getModules({ all: true });
        if (response && response.success && response.data) {
          sortableModules.value = [...(response.data.items || response.data || [])];
        }
      } catch (err) {
        error.value = err.message || '載入失敗';
        return;
      } finally {
        loading.value = false;
      }
      sortMode.value = true;
    };

    const cancelSortMode = () => {
      sortMode.value = false;
      sortableModules.value = [];
    };

    const saveSortOrder = async () => {
      saving.value = true;
      try {
        const items = sortableModules.value.map((m, index) => ({
          id: m.id,
          sortOrder: (index + 1) * 10,
        }));
        const response = await moduleService.reorder(items);
        if (response && response.success) {
          await showSuccess('儲存成功', '模組排序已更新');
          sortMode.value = false;
          sortableModules.value = [];
          await fetchModules();
        } else {
          await showError('儲存失敗', response?.message || '排序更新失敗');
        }
      } catch (err) {
        await showError('儲存失敗', err.message || '排序更新失敗');
      } finally {
        saving.value = false;
      }
    };

    const handleCreate = () => {
      router.push('/system/modules/create');
    };

    const handleEdit = (module) => {
      // 保存當前頁碼到 URL query，編輯完返回時可以恢復
      router.push({
        path: `/system/modules/${module.id}/edit`,
        query: { returnPage: currentPage.value }
      });
    };

    const handleDelete = async (module) => {
      const confirmed = await showDeleteConfirm(`${module.moduleName} (${module.moduleCode})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await moduleService.deleteModule(module.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `模組「${module.moduleName}」已成功刪除`);
          
          if (modules.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchModules();
        } else {
          const errorMsg = response?.message || '刪除模組失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除模組失敗:', err);
        let errorTitle = '刪除失敗';
        let errorText = '刪除模組失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '模組不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此模組';
        } else if (err.response?.status === 404) {
          errorText = '模組不存在或路由錯誤';
        } else if (err.response?.status === 403) {
          errorText = '您沒有權限刪除此模組';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      // 從 URL query 恢復頁碼（編輯完返回時）
      const pageFromQuery = parseInt(route.query.page);
      if (pageFromQuery && pageFromQuery > 0) {
        currentPage.value = pageFromQuery;
      }
      fetchModules();
    });

    // 監聽 route 變化，當從編輯頁返回時恢復頁碼
    watch(() => route.query.page, (newPage) => {
      if (newPage) {
        const page = parseInt(newPage);
        if (page > 0 && page !== currentPage.value) {
          currentPage.value = page;
          fetchModules();
        }
      }
    });

    return {
      modules,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      pageTitle,
      sortMode,
      sortableModules,
      saving,
      dragIndex,
      dragOverIndex,
      handleSearch,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      getModuleVisibleAccountTypes,
      isModuleOptional,
      MODULE_ACCOUNT_TYPE_LABELS,
      MODULE_ACCOUNT_TYPE_BADGE_CLASS,
      fetchModules,
      enterSortMode,
      cancelSortMode,
      saveSortOrder,
      onDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1000px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-outline {
  background: transparent;
  border: 1px solid #667eea;
  color: #667eea;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #667eea;
  color: #fff;
}

.sort-mode-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.drag-col {
  width: 40px;
  text-align: center;
}

.drag-handle {
  font-size: 18px;
  color: #9ca3af;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;
}

.drag-handle:hover {
  color: #667eea;
}

.drag-handle:active {
  cursor: grabbing;
}

.draggable-row {
  transition: background 0.15s;
  cursor: grab;
}

.draggable-row:hover {
  background: #f8f9ff;
}

.draggable-row.dragging {
  opacity: 0.4;
  background: #e0e7ff;
}

.draggable-row.drag-over {
  background: #eff6ff;
  border-top: 2px solid #667eea;
}

.module-name-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.module-icon {
  font-size: 16px;
  color: #667eea;
  flex-shrink: 0;
}

.badge-group {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
</style>
